<template>
  <OverviewTab
    :market="market"
    :initial-fields="initialFields"
    :services="services"
    :title="titlePackage"
    :intro="intro"
  />
</template>

<script setup lang="ts">
import OverviewTab from '~/components/sustainability/OverviewTab.vue'
import type { TMarketServiceConfig } from '~/src/types/markets'
import { useSeo } from '~/composables/useSeo'

/*-------------------------------------
  Constants
-------------------------------------*/
const intro = {
  title:
    'Benchmark’s Lithium ESG Report is an annual subscription with quarterly updates.',
  body: [
    'Benchmark’s Lithium ESG Report is an annual subscription with quarterly updates.',
    'The report offers comprehensive coverage of the evolving ESG landscape in the upstream lithium supply chain, forecasted trends, lithium carbon intensity curves and our Benchmark Global LCAs, which measure the environmental impact of producing both lithium carbonate and lithium hydroxide.',
    'To keep subscribers up to date, the report contains individual assessments of all lithium miners and converters, with indicators measuring ESG performance alongside tiers allocated based on our Benchmark’s Sustainability Index Methodology. Utilising data collected through Benchmark’s independent research and analysis, this report provides detailed and expert ESG assessment and monitoring of the lithium industry.',
  ],
  images: {
    one: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,c_crop,h_1400,g_auto,w_400,dpr_auto/v1731423773/assets/esg/lithium/benchmarkminerals_geometric_lithium_open_mine_blue_sky_photorea_7be92f0f-a292-4bb4-9776-ab14db3191b3_kznnde.png',
      alt: 'Lithium ESG',
    },
    two: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,c_crop,h_800,g_auto,w_400,dpr_auto/v1731423774/assets/esg/lithium/benchmarkminerals_geometric_lithium_mine_in_the_valley_blue_sky_34adcdb3-c7c1-4ade-988d-b4e69a435796_w5whos.png',
      alt: 'Lithium ESG',
    },
    three: {
      url: 'https://res.cloudinary.com/benchmarkminerals/image/upload/f_auto,q_auto,c_crop,h_800,g_auto,w_400,dpr_auto/v1731423771/assets/esg/lithium/benchmarkminerals_AA_batteries_photo_photorealistic_Hyper-reali_3b8bfcd3-533c-4f54-9447-ad2442be6e94_qwz6gf.png',
      alt: 'Lithium ESG',
    },
  },
}

const services = [
  {
    icon: 'services/icon-success-chart',
    description: 'Company-by-company ESG database',
  },
  {
    icon: 'services/icon-presentation',
    description: 'Materiality assessment and critical ESG analysis',
  },
  {
    icon: 'services/icon-fragment-chart',
    description: 'Lithium carbon intensity curves',
  },
  {
    icon: 'services/icon-eco-earth',
    description: 'Benchmark Global Lithium LCA',
  },
  {
    icon: 'services/icon-consultancy',
    description: 'ESG news brief and global policy review',
  },
  {
    icon: 'services/icon-idea',
    description: 'Access to Benchmark’s analysts',
  },
]

/*-------------------------------------
  Meta
-------------------------------------*/
definePageMeta({
  isTab: true,
  scrollToTop: false,
  middleware: 'redirect-tab',
})

/*-------------------------------------
  Props & Refs
-------------------------------------*/
const props = defineProps<{
  market: TMarketServiceConfig
  initialFields: string[]
}>()

const titlePackage = ref(`${props.market.name} ESG Report`)

/*-------------------------------------
  SEO
-------------------------------------*/
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: 'Lithium Sustainability Analysis & ESG Reports',
  description:
    'Benchmark’s Lithium ESG Report is an annual subscription with quarterly updates. Subscribers can assess and benchmark the ESG performance of lithium producers around the world.',
})
useHead(seo)
</script>
